import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, message, Modal, Popconfirm, Skeleton, Space, Tag, Typography } from 'antd';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi } from '../../../services/ai-admin-api';
import { ModelBeliefsRead } from '../../../components/MarketerView/components/modelBeliefs/ModelBeliefsRead';

const { Text } = Typography;

// What the customer would see for this run, rendered by the customer's own
// component rather than an admin reimplementation of it — the same reason the
// public result view is reused for the survey preview. A preview that drifts
// from the page is worse than no preview, because it is believed.
//
// Opens in a full-page modal: the read is a long page with wide tables, and
// inside an expanded table row it had the width of a column.
//
// Loaded on demand: the read is a heavy payload and a runs table does not need
// one per row.
export const ModelBeliefsReadPreview: React.FC<{ runId: number }> = ({ runId }) => {
  const { user } = useAuth();
  const jwt = user?.jwtToken ?? '';

  const [open, setOpen] = useState(false);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!jwt) return;
    setLoading(true);
    setError(null);
    try {
      setData(await aiAdminApi.getModelBeliefsRunRead(jwt, runId));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [jwt, runId]);

  useEffect(() => {
    if (open && !data && !loading) void load();
  }, [open, data, loading, load]);

  // The run's numbers stay as they are; only the written read is made again,
  // on the brand's current config. The way to see a changed price positioning,
  // or a published prompt change, on a read already paid for.
  const [rewriting, setRewriting] = useState(false);
  const rewrite = async () => {
    if (!jwt) return;
    setRewriting(true);
    try {
      await aiAdminApi.synthesiseModelBeliefsRun(jwt, runId);
      message.success(`Run #${runId}: the read has been written again`);
      setData(null);
      await load();
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setRewriting(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        icon={<EyeOutlined style={{ marginRight: 4 }} />}
        onClick={() => setOpen(true)}
      >
        Preview the read
      </Button>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width="100vw"
        style={{ top: 0, maxWidth: '100vw', paddingBottom: 0 }}
        styles={{ body: { height: 'calc(100vh - 56px)', overflowY: 'auto', background: '#F8FAFC', padding: '16px 24px' } }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingRight: 32 }}>
            <span>Run #{runId}</span>
            <Tag color="blue">customer view</Tag>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400, flex: '1 1 auto' }}>
              Rendered by the customer page’s own component. Action Plan toggles are inert here.
            </Text>
            {data?.latest && (
              <Popconfirm
                title="Re-synthesise?"
                description="Writes the read again from this run's numbers with the reviewer pass, on the target's current config. The numbers are untouched. About forty cents."
                okText="Re-synthesise"
                onConfirm={() => void rewrite()}
              >
                <Button size="small" icon={<EditOutlined style={{ marginRight: 4 }} />} loading={rewriting}>
                  Re-synthesise
                </Button>
              </Popconfirm>
            )}
          </div>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {error && <Alert type="error" showIcon message={error} />}
          {loading && !data && <Skeleton active paragraph={{ rows: 8 }} />}

          {data?.adHoc && (
            <Alert
              type="info"
              showIcon
              message="Ad-hoc target"
              description="This run is not a platform brand, so there is no customer survey to sit beside it and no Action Plan to commit a record fix to. Everything else is what a customer would see."
            />
          )}

          {data?.latest && (
            <div style={{ maxWidth: 1180, margin: '0 auto', width: '100%' }}>
              <ModelBeliefsRead
                latest={data.latest}
                history={data.history as any}
                brandName={data.latest?.consumerName ?? `Brand ${data.brandId ?? ''}`.trim()}
                readOnly
              />
            </div>
          )}

          {data && !data.latest && (
            <Alert
              type="warning"
              showIcon
              message="Nothing to preview"
              description="This run produced no results — it was cancelled, failed, or has not finished aggregating."
            />
          )}
        </Space>
      </Modal>
    </>
  );
};
